import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Search, Person, Info } from "@mui/icons-material";
import { toast } from "react-toastify";
import SearchableSelect from "./dashboard/components/SearchableSelect";
import UnassignReassignModal from "./dashboard/components/UnassignReassignModal";
import { useAuth } from '../context/AuthContext';

const AssignedLoans = () => {
  const { isManager } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const buckets = [
    { label: "1-7 Days", value: { min: 1, max: 7 } },
    { label: "8-14 Days", value: { min: 8, max: 14 } },
    { label: "15-21 Days", value: { min: 15, max: 21 } },
    { label: "22-30 Days", value: { min: 22, max: 30 } },
    { label: "30+ Days", value: { min: 30, max: 100 } },
  ];

  // Hydrate initial filter state from URL — survives back-navigation from a loan detail page
  const initialBucket = (() => {
    const raw = searchParams.get("bucket");
    if (!raw) return "";
    try { return JSON.parse(raw); } catch (e) { return ""; }
  })();

  const [loans, setLoans] = useState([]);
  const [officers, setOfficers] = useState([]);
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [selectedBucket, setSelectedBucket] = useState(initialBucket);
  const [minAmount, setMinAmount] = useState(searchParams.get("minAmount") || "");
  const [maxAmount, setMaxAmount] = useState(searchParams.get("maxAmount") || "");
  const [selectedOfficer, setSelectedOfficer] = useState(searchParams.get("officer") || "");
  const [selectedLoans, setSelectedLoans] = useState([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingOfficers, setLoadingOfficers] = useState(false);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get("page") || "1", 10) || 1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalArrears, setTotalArrears] = useState(0);
  const loansPerPage = 20;

  const totalPages = Math.ceil(totalCount / loansPerPage);

  // Format currency helper
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "$0.00";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Format number helper
  const formatNumber = (value) => {
    if (value === null || value === undefined) return "0";
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Resolution type label helper
  const getResolutionTypeLabel = (type) => {
    const labels = {
      client_picked_up: "Client Picked Up",
      client_no_answer: "Client Didn't Pick Up",
      promise_to_pay: "Promise to Pay",
      payment_collected: "Payment Collected",
      resolved: "Resolved",
      wrong_number: "Wrong Number",
      dispute: "Dispute",
      other: "Other"
    };
    return labels[type] || type;
  };

  // Resolution badge color helper
  const getResolutionBadgeColor = (type) => {
    const colors = {
      client_picked_up: "bg-green-100 text-green-800",
      client_no_answer: "bg-yellow-100 text-yellow-800",
      promise_to_pay: "bg-blue-100 text-blue-800",
      payment_collected: "bg-green-100 text-green-800",
      resolved: "bg-purple-100 text-purple-800",
      wrong_number: "bg-red-100 text-red-800",
      dispute: "bg-orange-100 text-orange-800",
      other: "bg-gray-100 text-gray-800"
    };
    return colors[type] || "bg-gray-100 text-gray-800";
  };

  // Format date for compact display
  const formatCompactDate = (date) => {
    if (!date) return "N/A";
    try {
      const d = new Date(date);
      const now = new Date();
      const diffMs = now - d;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 60) {
        return `${diffMins}m ago`;
      } else if (diffHours < 24) {
        return `${diffHours}h ago`;
      } else if (diffDays === 1) {
        return 'Yesterday';
      } else if (diffDays < 7) {
        return `${diffDays}d ago`;
      } else {
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
    } catch (e) {
      return "N/A";
    }
  };

  // Fetch officers
  const fetchOfficers = async () => {
    setLoadingOfficers(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST_COLLECTIONS}/collections-officers`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      const officersList = data.collectionsOfficers || [];
      // Sort officers by first name alphabetically
      const sortedOfficers = officersList.sort((a, b) => {
        const firstNameA = (a.firstName || "").toLowerCase();
        const firstNameB = (b.firstName || "").toLowerCase();
        return firstNameA.localeCompare(firstNameB);
      });
      setOfficers(sortedOfficers);
    } catch (error) {
      console.error("Error fetching officers:", error);
    } finally {
      setLoadingOfficers(false);
    }
  };

  // Fetch loans
  const previousCountRef = useRef(null);
  const fetchLoans = async (silent = false) => {
    if (!silent) {
      setLoading(true);
    }
    try {
      const previousCount = loans.length;

      const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : "";
      const bucketParam = selectedBucket ? `&bucket=${encodeURIComponent(JSON.stringify(selectedBucket))}` : "";
      const minAmountParam = minAmount ? `&minAmount=${encodeURIComponent(minAmount)}` : "";
      const maxAmountParam = maxAmount ? `&maxAmount=${encodeURIComponent(maxAmount)}` : "";
      const officerParam = selectedOfficer ? `&officerId=${encodeURIComponent(selectedOfficer)}` : "";

      const response = await fetch(
        `${process.env.REACT_APP_API_HOST_COLLECTIONS}/assigned-loans?page=${currentPage}&limit=${loansPerPage}${searchParam}${bucketParam}${minAmountParam}${maxAmountParam}${officerParam}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      const newLoans = data.data || [];
      const newCount = newLoans.length;

      setLoans(newLoans);
      setTotalCount(data.pagination?.totalCount || 0);
      setTotalArrears(data.totalArrears || 0);

      // If current page is beyond the filtered results, snap back to page 1
      const totalCountValue = data.pagination?.totalCount || 0;
      if (newCount === 0 && totalCountValue > 0 && currentPage > 1) {
        setCurrentPage(1);
      }

      // Show notification if loans were removed during silent refresh
      if (silent && previousCountRef.current !== null && previousCountRef.current > newCount) {
        const removedCount = previousCountRef.current - newCount;
        toast.info(`${removedCount} loan(s) removed from collections (payment received)`);
      }

      previousCountRef.current = newCount;
    } catch (error) {
      console.error("Error fetching loans:", error);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  // Keep the URL in sync with current filters so back-navigation restores them
  useEffect(() => {
    const params = {};
    if (searchTerm) params.search = searchTerm;
    if (selectedBucket) params.bucket = JSON.stringify(selectedBucket);
    if (minAmount) params.minAmount = minAmount;
    if (maxAmount) params.maxAmount = maxAmount;
    if (selectedOfficer) params.officer = selectedOfficer;
    if (currentPage > 1) params.page = String(currentPage);
    setSearchParams(params, { replace: true });
  }, [searchTerm, selectedBucket, minAmount, maxAmount, selectedOfficer, currentPage, setSearchParams]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchLoans();
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchLoans();
  }, [currentPage, selectedBucket, minAmount, maxAmount, selectedOfficer]);

  useEffect(() => {
    fetchOfficers();
  }, []);

  // Auto-refresh loans every 30 seconds to detect payment-based removals
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchLoans(true); // Silent refresh
    }, 30000); // 30 seconds

    return () => clearInterval(intervalId);
  }, [currentPage, selectedBucket, minAmount, maxAmount, selectedOfficer, searchTerm]);

  // Handle select all (current page only)
  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedLoans([]);
      setIsAllSelected(false);
    } else {
      const allLoanIds = loans.map((loan) => loan._id);
      setSelectedLoans(allLoanIds);
      setIsAllSelected(true);
    }
  };

  // Handle select ALL matching loans (not just current page)
  const handleSelectAllMatching = async () => {
    if (selectAllMode) {
      // Clear selection
      setSelectedLoans([]);
      setIsAllSelected(false);
      setSelectAllMode(false);
    } else {
      // Enable "select all matching" mode
      setSelectAllMode(true);
      setIsAllSelected(true);

      // Fetch first 1000 IDs for UI feedback
      setLoading(true);
      try {
        const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : "";
        const bucketParam = selectedBucket ? `&bucket=${encodeURIComponent(JSON.stringify(selectedBucket))}` : "";
        const minAmountParam = minAmount ? `&minAmount=${minAmount}` : "";
        const maxAmountParam = maxAmount ? `&maxAmount=${maxAmount}` : "";
        const officerParam = selectedOfficer ? `&officerId=${selectedOfficer}` : "";

        const response = await fetch(
          `${process.env.REACT_APP_API_HOST_COLLECTIONS}/assigned-loans?limit=1000${searchParam}${bucketParam}${minAmountParam}${maxAmountParam}${officerParam}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${localStorage.accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        setSelectedLoans(data.data.map(loan => loan._id));
      } catch (error) {
        console.error("Error fetching loan IDs:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle individual loan selection
  const handleLoanSelection = (loanId) => {
    setSelectedLoans((prev) =>
      prev.includes(loanId)
        ? prev.filter((id) => id !== loanId)
        : [...prev, loanId]
    );
    setIsAllSelected(false);
  };

  // Update isAllSelected when loans change
  useEffect(() => {
    if (loans.length > 0) {
      setIsAllSelected(selectedLoans.length === loans.length && loans.every((loan) => selectedLoans.includes(loan._id)));
    }
  }, [selectedLoans, loans]);

  // Handle action success
  const handleActionSuccess = () => {
    setSelectedLoans([]);
    setIsAllSelected(false);
    setSelectAllMode(false);
    fetchLoans();
  };

  const handleLoanClick = (loanId) => {
    // Preserve current filters/page in the return path
    const returnPath = `/assigned-loans${location.search || ''}`;
    navigate(`/loan/${loanId}`, {
      state: {
        from: 'Assigned Loans',
        returnPath
      }
    });
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Person className="text-cyan-600" />
            <h1 className="text-3xl font-bold text-gray-900">Assigned Loans</h1>
          </div>
          <p className="text-gray-600">
            {isManager
              ? "Loans that have been assigned to collections officers"
              : "Your assigned loans for collections"}
          </p>
        </div>

        {/* Agent Info Banner */}
        {!isManager && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <Info className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-800 font-medium">
                Viewing your assigned loans only
              </p>
              <p className="text-xs text-blue-600 mt-1">
                The loans below are filtered to show only those assigned to you.
                Bulk actions are not available in this view.
              </p>
            </div>
          </div>
        )}

        {/* Filters Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h3 className="font-bold text-lg mb-4 text-gray-700">Filters</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {/* Search */}
            <div>
              <label className="block font-bold mb-2 text-gray-700 text-sm">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Loan # or name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
            </div>

            {/* Bucket Filter */}
            <div className="flex flex-col">
              <SearchableSelect
                label="Bucket"
                options={[
                  { value: "", label: "All" },
                  ...buckets.map((bucket) => ({
                    value: bucket.value.min.toString(),
                    label: bucket.label,
                    bucket: bucket,
                  })),
                ]}
                value={selectedBucket && selectedBucket.value && selectedBucket.value.min !== undefined ? selectedBucket.value.min.toString() : ""}
                onChange={(value) => {
                  if (value === "") {
                    setSelectedBucket("");
                  } else {
                    const bucket = buckets.find(
                      (b) => b.value.min.toString() === value
                    );
                    setSelectedBucket(bucket || "");
                  }
                  setCurrentPage(1);
                }}
                placeholder="All"
                searchable={true}
              />
            </div>

            {/* Amount Filters */}
            <div>
              <label className="block font-bold mb-2 text-gray-700 text-sm">Min Amount</label>
              <input
                type="number"
                placeholder="Min Amount"
                value={minAmount}
                onChange={(e) => {
                  setMinAmount(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                min="0"
                step="0.01"
              />
            </div>

            <div>
              <label className="block font-bold mb-2 text-gray-700 text-sm">Max Amount</label>
              <input
                type="number"
                placeholder="Max Amount"
                value={maxAmount}
                onChange={(e) => {
                  setMaxAmount(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          {/* Officer Filter - Only for managers */}
          {isManager && (
            <div>
              <label className="block font-bold mb-2 text-gray-700 text-sm">Filter by Officer</label>
              {loadingOfficers ? (
                <div className="text-gray-500 text-sm">Loading Officers...</div>
              ) : officers.length === 0 ? (
                <div className="text-red-500 text-sm">No officers found. Please ensure agents are assigned to you in the admin portal.</div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      setSelectedOfficer("");
                      setCurrentPage(1);
                    }}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      !selectedOfficer
                        ? "bg-blue-500 text-white hover:bg-blue-600"
                        : "bg-blue-100 text-gray-700 hover:bg-blue-200"
                    }`}
                  >
                    All
                  </button>
                  {officers.map((officer) => {
                    const displayName = officer.firstName;
                    const isSelected = selectedOfficer === officer._id;
                  
                  return (
                    <button
                      key={officer._id}
                      onClick={() => {
                        setSelectedOfficer(isSelected ? "" : officer._id);
                        setCurrentPage(1);
                      }}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                        isSelected
                          ? "bg-blue-500 text-white hover:bg-blue-600"
                          : "bg-blue-100 text-gray-700 hover:bg-blue-200"
                      }`}
                    >
                      {displayName}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          )}

          {/* Clear Filters */}
          {(selectedBucket || minAmount || maxAmount || selectedOfficer) && (
            <button
              onClick={() => {
                setSelectedBucket("");
                setMinAmount("");
                setMaxAmount("");
                setSelectedOfficer("");
                setCurrentPage(1);
              }}
              className="mt-4 text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Clear All Filters
            </button>
          )}
        </div>

        {/* Select All Mode Indicator */}
        {selectAllMode && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-blue-800 text-sm">
              <strong>Selecting all {totalCount.toLocaleString()} matching loans</strong>
              {" - "}The first 1,000 are shown here. All matching loans will be unassigned/reassigned.
            </p>
          </div>
        )}

        {/* Action Buttons - Only for managers */}
        {isManager && (selectedLoans.length > 0 || selectAllMode) && (
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-2">
                <span className="text-gray-700 font-medium">
                  {selectAllMode ? `${totalCount.toLocaleString()} loan(s) selected (all matching)` : `${selectedLoans.length} loan(s) selected`}
                </span>
                {/* Option to select all matching loans - only show if some selected but not in selectAllMode and totalCount > selected */}
                {!selectAllMode && selectedLoans.length > 0 && totalCount > selectedLoans.length && (
                  <button
                    onClick={handleSelectAllMatching}
                    className="text-sm text-blue-600 hover:text-blue-800 underline text-left"
                  >
                    Select all {totalCount.toLocaleString()} matching loans instead?
                  </button>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setActionModalOpen(true)}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 font-medium"
                >
                  Unassign / Reassign
                </button>
                <button
                  onClick={() => {
                    setSelectedLoans([]);
                    setIsAllSelected(false);
                    setSelectAllMode(false);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Clear Selection
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-sm text-gray-600 mb-1">Assigned Loans</p>
            <p className="text-2xl font-bold text-gray-900">{formatNumber(totalCount)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-sm text-gray-600 mb-1">Total Arrears</p>
            <p className="text-2xl font-bold text-cyan-600">
              {formatCurrency(totalArrears)}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-sm text-gray-600 mb-1">Average Arrears</p>
            <p className="text-2xl font-bold text-gray-900">
              {totalCount > 0
                ? formatCurrency(totalArrears / totalCount)
                : "$0.00"}
            </p>
          </div>
        </div>

        {/* Loans Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : loans.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No assigned loans found.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {/* Checkbox column - only for managers */}
                      {isManager && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 cursor-pointer"
                            checked={(isAllSelected && loans.length > 0) || selectAllMode}
                            onChange={handleSelectAll}
                            disabled={selectAllMode}
                            title={selectAllMode ? "All matching loans selected" : "Select all on this page"}
                          />
                        </th>
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Loan #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Client Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Assigned To
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Late Days
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Arrears
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Call
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loans.map((loan) => (
                      <tr
                        key={loan._id}
                        className={`hover:bg-gray-50 transition-colors ${!isManager ? 'opacity-75' : ''}`}
                      >
                        {/* Checkbox cell - only for managers */}
                        {isManager && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              className="rounded border-gray-300 cursor-pointer"
                              checked={selectedLoans.includes(loan._id)}
                              onChange={() => handleLoanSelection(loan._id)}
                            />
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleLoanClick(loan._id)}
                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium cursor-pointer"
                          >
                            {loan.loanNumber}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {loan.client?.personal?.fullName || "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {loan.assignment?.assignedAgentName 
                            ? loan.assignment.assignedAgentName
                            : loan.assignment?.assignedAgentId
                            ? `${loan.assignment.assignedAgentId.firstName || ""} ${loan.assignment.assignedAgentId.lastName || ""}`.trim() || "N/A"
                            : "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                          {formatNumber(loan.lateDays)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                          {formatCurrency(
                            loan.due?.totalAllDue || loan.due?.totalDue || 0
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {loan.lastResolution ? (
                            <div className="flex flex-col gap-1">
                              {/* Consolidated Promise Badge or Regular Resolution Badge */}
                              {loan.lastResolution.resolutionType === 'promise_to_pay' ? (
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  loan.lastResolution.promiseStatus === 'fulfilled' ? 'bg-green-100 text-green-800' :
                                  loan.lastResolution.promiseStatus === 'broken' ? 'bg-red-100 text-red-800' :
                                  loan.lastResolution.promiseStatus === 'partially_fulfilled' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {loan.lastResolution.promiseStatus === 'fulfilled' ? 'Promise Fulfilled' :
                                   loan.lastResolution.promiseStatus === 'broken' ? 'Promise Broken' :
                                   loan.lastResolution.promiseStatus === 'partially_fulfilled' ? `Promise Partial (${loan.lastResolution.fulfillmentPercentage}%)` :
                                   'Promise Pending'}
                                </span>
                              ) : (
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getResolutionBadgeColor(loan.lastResolution.resolutionType)}`}>
                                  {getResolutionTypeLabel(loan.lastResolution.resolutionType)}
                                </span>
                              )}
                              <span className="text-xs text-gray-500">
                                {formatCompactDate(loan.lastResolution.createdAt)}
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">No calls yet</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing page <span className="font-medium">{currentPage}</span> of{" "}
                        <span className="font-medium">{totalPages}</span>
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Unassign/Reassign Modal */}
        <UnassignReassignModal
          isOpen={actionModalOpen}
          onClose={() => setActionModalOpen(false)}
          selectedLoans={selectedLoans}
          selectAllMode={selectAllMode}
          totalCount={totalCount}
          filters={{
            bucket: selectedBucket,
            minAmount: minAmount || null,
            maxAmount: maxAmount || null,
            search: searchTerm || null,
            officerId: selectedOfficer || null
          }}
          onSuccess={handleActionSuccess}
        />
      </div>
    </div>
  );
};

export default AssignedLoans;

